import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Smartphone } from 'lucide-react';
import OrderStatusPill from '../shared/OrderStatusPill';
import { isHoldStatus } from '../shared/orderStatus';
import StatusSelect from '../detail/StatusSelect';
import { CopyButton } from '../detail/primitives';
import { CURRENCY, formatAmount, formatDateTime } from '../shared/orderFormat';
import { tamexTrackUrl } from '../shared/orderConstants';

/*
 * One column set, used by every tab.
 *
 * Previously each tab hand-picked its own list, ranging from eight columns to
 * twelve, so the table changed shape as you switched between them and the wide
 * tabs scrolled sideways. Address, Admin note, Delivery image and Delivery
 * date all moved into the preview drawer, which has room for them.
 *
 * Each column carries a `skeleton` descriptor so the loading placeholder is
 * generated from the same definition as the real row and the two cannot drift
 * out of alignment.
 */

const Chip = ({ label, tone = 'neutral' }) => {
  const tones = {
    neutral: 'text-[#3B4A54] border-[#D6DFE2] bg-[#EFF3F4]',
    success: 'text-[#256B4C] border-[#AFD6C2] bg-[#E7F4ED]',
    info: 'text-[#33586A] border-[#BDD3DC] bg-[#E9F1F4]',
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-[2px] rounded text-[9.5px] font-bold tracking-[0.04em] border ${tones[tone]}`}
    >
      {label}
    </span>
  );
};

const PAYMENT_TONE = {
  0: { dot: '#D4A017', text: 'text-[#7A5A07]', label: 'Pending' },
  1: { dot: '#2E7D5B', text: 'text-[#256B4C]', label: 'Paid' },
  2: { dot: '#D14343', text: 'text-[#B23A32]', label: 'Failed' },
  3: { dot: '#8A9BA8', text: 'text-[#6B7C86]', label: 'Abandoned' },
};

// order_from: 1 = iOS, 2 = Android, 3 = Web. The label comes from the
// backend's order_from_label; only the icon is picked from the numeric.
const ORIGIN_ICON = { 1: Smartphone, 2: Smartphone, 3: Globe };

export const buildOrderColumns = ({
  statusOptions,
  isEditable,
  canManageHold,
  pendingIds,
  onStatusChange,
  transitionsFor,
}) => [
  {
    key: 'order',
    header: 'Order # / Customer',
    skeleton: { width: '70%', lines: 2, subWidth: '50%' },
    // The order number stays a real anchor even though the whole row is now
    // clickable: it is what makes cmd-click, middle-click and "open in new tab"
    // work, puts the target url in the status bar on hover, and gives screen
    // readers something announced as a link. The row handler is the
    // convenience layer over it, not a replacement.
    cell: (order) => (
      <td className="relative px-3.5 py-2.5">
        <div className="flex flex-col gap-0.5">
          <Link
            to={`/orderview/${order.order_id}`}
            className="w-fit text-[13px] font-bold text-nysmah-dark tabular-nums no-underline hover:underline after:absolute after:inset-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent"
          >
            {order.order_number}
          </Link>
          <span className="text-[11.5px] text-[#6B7C86]">
            {order.customer || '—'}
          </span>
          {Boolean(order.is_first_order) && (
            <span className="mt-0.5">
              <Chip label="FIRST ORDER" tone="success" />
            </span>
          )}
        </div>
      </td>
    ),
  },
  {
    key: 'placed',
    header: 'Placed',
    skeleton: { width: '80%' },
    cell: (order) => (
      <td className="px-3.5 py-2.5 whitespace-nowrap text-[12.5px] font-medium text-[#3B4A54] tabular-nums">
        {formatDateTime(order.created_on)}
      </td>
    ),
  },
  {
    key: 'origin',
    header: 'Origin',
    skeleton: { width: '55%' },
    cell: (order) => {
      const label = order.order_from_label;
      const Icon = ORIGIN_ICON[order.order_from] || Globe;
      return (
        <td className="px-3.5 py-2.5 whitespace-nowrap">
          {label ? (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#3B4A54]">
              <Icon className="w-3.5 h-3.5 text-[#6B7C86]" aria-hidden="true" />
              {label}
            </span>
          ) : (
            <span className="text-[#B6C2C8]">—</span>
          )}
        </td>
      );
    },
  },
  {
    key: 'mobile',
    header: 'Mobile',
    skeleton: { width: '75%' },
    // Checkout always collects a number with the delivery address; the account
    // number is only a fallback for orders placed without one.
    cell: (order) => {
      const fromAddress = Boolean(order.contact_number);
      const number = fromAddress ? order.contact_number : order.mobile_number;
      const code = fromAddress
        ? order.contact_country_code
        : order.country_code;
      const full = number ? `${code ? `${code} ` : ''}${number}` : null;
      return (
        <td className="px-3.5 py-2.5 whitespace-nowrap">
          {full ? (
            <span className="inline-flex items-center gap-1 group/phone">
              <span className="text-[12.5px] font-medium text-[#3B4A54] tabular-nums">
                {full}
              </span>
              <CopyButton value={full} label="Copy mobile number" />
            </span>
          ) : (
            <span className="text-[#B6C2C8]">—</span>
          )}
        </td>
      );
    },
  },
  {
    key: 'total',
    header: `Total (${CURRENCY})`,
    align: 'right',
    skeleton: { width: '60%', align: 'right' },
    cell: (order) => (
      <td className="px-3.5 py-2.5 text-right whitespace-nowrap">
        <span className="text-[13px] font-bold text-nysmah-dark tabular-nums">
          {formatAmount(order.final_price)}
        </span>
        {order.coupon_code && (
          <span className="ml-1.5 align-middle">
            <Chip label="COUPON" tone="info" />
          </span>
        )}
      </td>
    ),
  },
  {
    key: 'payment',
    header: 'Payment',
    skeleton: { width: '65%', lines: 2, subWidth: '40%' },
    // Method and status were two columns showing one fact between them.
    cell: (order) => {
      const tone = PAYMENT_TONE[order.payment_status];
      const method = order.payment_method_label || order.paymentMethod;
      return (
        <td className="px-3.5 py-2.5 whitespace-nowrap">
          <span className="flex flex-col gap-0.5">
            <span className="text-[12.5px] font-semibold text-nysmah-dark">
              {method || '—'}
            </span>
            {tone && (
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${tone.text}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: tone.dot }}
                  aria-hidden="true"
                />
                {tone.label}
              </span>
            )}
          </span>
        </td>
      );
    },
  },
  {
    key: 'status',
    header: 'Status',
    skeleton: { width: '80%' },
    cell: (order, { statusLabelById }) => {
      // A held order is frozen for everyone but a super admin, so the picker
      // becomes a pill rather than a control that can only fail.
      const locked = isHoldStatus(order.status) && !canManageHold;
      const options = transitionsFor ? transitionsFor(order) : statusOptions;
      return (
        <td className="px-3.5 py-2.5">
          {isEditable && !locked && options.length > 0 ? (
            <StatusSelect
              size="sm"
              value={order.status}
              options={options}
              onChange={(next) => onStatusChange(order, next)}
              pending={pendingIds.has(order.order_id)}
              ariaLabel={`Status for order ${order.order_number}`}
            />
          ) : (
            <OrderStatusPill
              statusId={order.status}
              label={statusLabelById.get(order.status)}
              size="sm"
            />
          )}
        </td>
      );
    },
  },
  {
    key: 'tracking',
    header: 'Tracking #',
    skeleton: { width: '70%' },
    cell: (order) => (
      <td className="px-3.5 py-2.5 whitespace-nowrap">
        {order.tracking_number ? (
          <a
            href={tamexTrackUrl(order.tracking_number)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="relative z-10 text-[12.5px] font-semibold text-nysmah-accent tabular-nums no-underline hover:underline"
          >
            {order.tracking_number}
          </a>
        ) : (
          <span className="text-[#B6C2C8]">—</span>
        )}
        {/* The trip back, when a returned order has its own waybill. */}
        {order.return_tracking_number && (
          <span className="block mt-0.5 text-[11.5px] text-[#6B7C86]">
            Return{' '}
            <a
              href={tamexTrackUrl(order.return_tracking_number)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="relative z-10 font-semibold text-nysmah-accent tabular-nums no-underline hover:underline"
            >
              {order.return_tracking_number}
            </a>
          </span>
        )}
      </td>
    ),
  },
];
